import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Phone, FileText, Bell, ClipboardCheck, CalendarClock, Trash2 } from "lucide-react";
import { toast } from "sonner";
import ScheduleVisitDialog from "@/components/ScheduleVisitDialog";

export default function Followups() {
  const [data, setData] = useState({ count: 0, items: [] });
  const [loading, setLoading] = useState(true);
  const [sched, setSched] = useState(null); // { patient_id, patient_name, next_visit_date }
  const [visitDate, setVisitDate] = useState("");
  const [visitTime, setVisitTime] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/followups").then((r) => setData(r.data || { count: 0, items: [] })).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openSchedule = (p) => { setSched(p); setVisitDate((p.next_visit_date || "").slice(0, 10)); setVisitTime(p.next_visit_time || ""); };

  const removeFollowup = async (p) => {
    if (!window.confirm(`Remove the scheduled follow-up for ${p.patient_name || "this patient"}?`)) return;
    try {
      await api.put(`/patients/${p.patient_id}/next-visit`, { next_visit_date: "" });
      toast.success("Follow-up removed");
      setData((d) => ({ count: Math.max(0, (d.count || 1) - 1), items: (d.items || []).filter((x) => x.patient_id !== p.patient_id) }));
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    }
  };
  const saveVisit = async () => {
    if (!sched) return;
    setSaving(true);
    try {
      await api.put(`/patients/${sched.patient_id}/next-visit`, { next_visit_date: visitDate, next_visit_time: visitTime });
      toast.success(visitDate ? "Next visit updated" : "Next visit cleared");
      setSched(null);
      load();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    } finally { setSaving(false); }
  };

  const items = Array.isArray(data?.items) ? data.items : [];

  return (
    <div className="p-6 md:p-8 max-w-5xl" data-testid="followups-page">
      <div className="mb-6">
        <h1 className="text-3xl font-semibold text-slate-900 tracking-tight flex items-center gap-3" style={{ fontFamily: "Work Sans" }}>
          <Bell size={26} className="text-amber-600" /> Follow-ups
        </h1>
        <p className="text-slate-500 text-sm mt-1">
          Upcoming scheduled follow-up visits. Past appointments drop off automatically — only today and future visits are shown.
        </p>
      </div>

      <Card className="border-slate-200">
        {loading ? (
          <div className="p-10 text-center text-slate-400">Loading...</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center">
            <ClipboardCheck size={32} className="mx-auto text-green-600 mb-2" />
            <div className="text-slate-700 font-medium">No upcoming follow-ups</div>
            <div className="text-xs text-slate-500 mt-1">
              Patients appear here once a future next-visit date is scheduled for them.
            </div>
          </div>
        ) : (
          <div className="divide-y divide-slate-200">
            {items.map((p) => (
              <div key={p.patient_id} className="p-5 flex flex-wrap items-center justify-between gap-4 hover:bg-slate-50" data-testid={`followup-row-${p.patient_id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="font-semibold text-slate-900 text-base">{p.patient_name || "(unknown)"}</div>
                    {p.next_visit_date ? (
                      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-amber-50 text-amber-700 font-medium">
                        Next visit: {p.next_visit_date}{p.next_visit_time ? ` at ${p.next_visit_time}` : ""}{p.no_show ? <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-bold">No show</span> : null}
                      </span>
                    ) : null}
                  </div>
                  <ul className="mt-2 space-y-1 text-sm text-slate-700">
                    {(p.reasons || []).map((r, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="text-amber-600 mt-0.5">•</span> {r}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-2 text-xs text-slate-500 flex items-center gap-4 flex-wrap">
                    {p.contact_number ? (
                      <span className="flex items-center gap-1"><Phone size={12} /> <span className="mono">{p.contact_number}</span></span>
                    ) : null}
                    {p.last_prescription ? (
                      <span className="flex items-center gap-1">
                        <FileText size={12} />
                        <span className="mono">{p.last_prescription.prescription_number}</span>
                        <span>· {p.last_prescription.medicines_count} med(s)</span>
                        {p.last_prescription.estimated_consumed_on ? (
                          <span>· consumed by {p.last_prescription.estimated_consumed_on}</span>
                        ) : null}
                      </span>
                    ) : null}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => openSchedule(p)} data-testid={`followup-schedule-${p.patient_id}`}>
                    <CalendarClock size={14} className="mr-1" /> {p.next_visit_date ? "Reschedule" : "Set visit"}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => removeFollowup(p)} className="text-red-600 border-red-200 hover:bg-red-50" data-testid={`followup-delete-${p.patient_id}`}>
                    <Trash2 size={14} className="mr-1" /> Delete
                  </Button>
                  {p.last_prescription ? (
                    <Button asChild variant="outline" size="sm">
                      <Link to={`/prescriptions/${p.last_prescription.id}/preview`}>View Last Rx</Link>
                    </Button>
                  ) : null}
                  <Button asChild className="bg-blue-600 hover:bg-blue-700" size="sm" data-testid={`followup-prescribe-${p.patient_id}`}>
                    <Link to={`/prescriptions/new?patient=${p.patient_id}`}>New Rx</Link>
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <ScheduleVisitDialog
        open={!!sched}
        onOpenChange={(o) => !o && setSched(null)}
        patientId={sched?.patient_id}
        patientName={sched?.patient_name}
        initialDate={sched?.next_visit_date || ''}
        initialTime={sched?.next_visit_time || ''}
        initialDoctorId={sched?.next_visit_doctor_id || ''}
        onSaved={load}
      />
    </div>
  );
}
